import _ from "lodash";

export const INLINE_VISUAL_OVERLAY_KEY = "_visualsByPath";
export const INLINE_VISUAL_OVERLAY_ROOT_PATH_KEY = "__root__";
export const AUTHORING_VISUAL_STORAGE_MODE = "inlineEntity";
export const OVERLAY_VISUAL_STORAGE_MODE = "inlineOverlay";

const ROW_ID_KEY_CANDIDATES = ["id", "value", "key", "slug", "guid", "objectID", "name"];
const LABEL_KEY_CANDIDATES = ["label", "value", "name", "title", "modelName", "communityName", "address", "id", "key"];
const VALUE_KEY_CANDIDATES = ["value", "id", "key", "slug", "guid", "objectID", "name"];
export const AUTHORING_COLLECTION_CANDIDATES = [
	{
		path: "collections.selectionGroupsById",
		displayLabel: "Selection Groups",
		description: "Canonical picker families compiled from grouped selection options.",
		rowIdKey: "id",
		labelKey: "label",
		valueKey: "id"
	},
	{
		path: "collections.selectionOptionsById",
		displayLabel: "Selection Options",
		description: "Canonical selectable options compiled into picker families.",
		rowIdKey: "id",
		labelKey: "label",
		valueKey: "value"
	},
	{
		path: "collections.communitiesById",
		displayLabel: "Communities",
		description: "Stable community entities shared across pricing and lot outputs.",
		rowIdKey: "id",
		labelKey: "value",
		valueKey: "id"
	},
	{
		path: "collections.pricingVariantsById",
		displayLabel: "Pricing Variants",
		description: "Pricing-phase or lot-range entities compiled into dependent picker variants.",
		rowIdKey: "id",
		labelKey: "value",
		valueKey: "id"
	},
	{
		path: "collections.modelsById",
		displayLabel: "Models",
		description: "Canonical home models shared across communities and pricing variants.",
		rowIdKey: "id",
		labelKey: "modelName",
		valueKey: "value"
	},
	{
		path: "collections.lotsById",
		displayLabel: "Lots",
		description: "Canonical lot entities compiled into pricing-variant lot lookups.",
		rowIdKey: "id",
		labelKey: "value",
		valueKey: "id"
	}
];

export function normalizeDatasourcePath(sourcePath = "") {
	if (!_.isString(sourcePath)) {
		return "";
	}
	let normalized = sourcePath.trim();
	if (_.startsWith(normalized, "$.")) {
		normalized = normalized.substring(2);
	} else if (_.startsWith(normalized, "$")) {
		normalized = normalized.substring(1);
	}
	if (_.startsWith(normalized, "/")) {
		normalized = normalized.substring(1).replace(/\//g, ".");
	}
	return normalized;
}

export function getInlineVisualOverlayPathKey(sourcePath = "") {
	const normalizedPath = normalizeDatasourcePath(sourcePath);
	return normalizedPath === "" ? INLINE_VISUAL_OVERLAY_ROOT_PATH_KEY : normalizedPath;
}

export function collectObjectRows(collection, options = {}) {
	const { annotateMapKeys = true } = options;
	const rows = [];
	if (_.isArray(collection)) {
		collection.forEach((entry) => {
			if (_.isPlainObject(entry)) {
				rows.push(entry);
			}
		});
	} else if (_.isPlainObject(collection)) {
		_.forEach(collection, (entry, mapKey) => {
			if (_.isArray(entry)) {
				entry.forEach((row) => {
					if (_.isPlainObject(row)) {
						const annotatedRow = annotateMapKeys && !row.hasOwnProperty("__mapKey") ? _.assign({}, row, { __mapKey: mapKey }) : row;
						rows.push(annotatedRow);
					}
				});
			} else if (_.isPlainObject(entry)) {
				const annotatedEntry = annotateMapKeys && !entry.hasOwnProperty("__mapKey") ? _.assign({}, entry, { __mapKey: mapKey }) : entry;
				rows.push(annotatedEntry);
			}
		});
	}
	return rows;
}

export function deriveCommonAndAllKeys(rows = []) {
	const allKeysMap = {};
	let commonKeys = null;
	rows.forEach((row) => {
		const keys = _.keys(row);
		keys.forEach((key) => {
			allKeysMap[key] = true;
		});
		if (commonKeys === null) {
			commonKeys = keys;
		} else {
			commonKeys = _.intersection(commonKeys, keys);
		}
	});
	return {
		allKeys: _.keys(allKeysMap),
		commonKeys: commonKeys || []
	};
}

export function isMapOfObjectArrays(value) {
	if (!_.isPlainObject(value) || _.isEmpty(value)) {
		return false;
	}
	let hasArray = false;
	let valid = true;
	_.forEach(value, (entry) => {
		if (!_.isArray(entry)) {
			valid = false;
			return false;
		}
		hasArray = true;
		if (_.some(entry, (row) => !_.isPlainObject(row))) {
			valid = false;
			return false;
		}
		return null;
	});
	return hasArray && valid;
}

export function getRowsFromMapOfObjectArrays(mapOfArrays, maxRows = 250) {
	const rows = [];
	_.forEach(mapOfArrays, (entry, mapKey) => {
		if (!_.isArray(entry)) {
			return;
		}
		_.forEach(entry, (row) => {
			if (rows.length >= maxRows) {
				return false;
			}
			if (_.isPlainObject(row)) {
				rows.push(row.hasOwnProperty("__mapKey") ? row : _.assign({}, row, { __mapKey: mapKey }));
			}
			return null;
		});
	});
	return rows;
}

export function collectDatasourcePathCandidates(content) {
	const candidates = [];
	const visited = {};

	const pushCandidate = (path, type, rows, groups = 0) => {
		if (_.isEmpty(rows)) {
			return;
		}
		const { allKeys, commonKeys } = deriveCommonAndAllKeys(rows);
		if (_.isEmpty(allKeys) && _.isEmpty(commonKeys)) {
			return;
		}
		const key = `${path}::${type}`;
		if (visited[key]) {
			return;
		}
		visited[key] = true;
		candidates.push({
			path,
			type,
			groups,
			rowCount: rows.length,
			allKeys,
			commonKeys,
			recommendedMode: type === "mapOfArrays" ? "mapByParentValue" : "none"
		});
	};

	const visit = (value, path = []) => {
		if (path.length === 1 && path[0] === INLINE_VISUAL_OVERLAY_KEY) {
			return;
		}
		const pathStr = path.join(".");
		if (_.isArray(value)) {
			const rows = collectObjectRows(value, { annotateMapKeys: true });
			if (!_.isEmpty(rows)) {
				pushCandidate(pathStr, "arrayOfObjects", rows);
			}
			return;
		}
		if (!_.isPlainObject(value)) {
			return;
		}

		if (isMapOfObjectArrays(value)) {
			const rows = getRowsFromMapOfObjectArrays(value);
			pushCandidate(pathStr, "mapOfArrays", rows, _.keys(value).length);
		}

		_.forEach(value, (child, key) => {
			if (path.length === 0 && key === INLINE_VISUAL_OVERLAY_KEY) {
				return;
			}
			if (_.isPlainObject(child) || _.isArray(child)) {
				visit(child, _.concat(path, [key]));
			}
		});
	};

	visit(content, []);
	return _.sortBy(candidates, (candidate) => `${candidate.path}::${candidate.type}`);
}

export function getDatasourcePathCandidateDisplay(candidate) {
	if (_.isString(candidate?.displayLabel) && candidate.displayLabel.trim() !== "") {
		return candidate.displayLabel;
	}
	const pathLabel = candidate.path && candidate.path !== "" ? candidate.path : "(root)";
	if (candidate.type === "mapOfArrays") {
		return `${pathLabel} - Map of arrays (${candidate.groups} groups, ${candidate.rowCount} sample rows)`;
	}
	return `${pathLabel} - Array of objects (${candidate.rowCount} sample rows)`;
}

export function isCanonicalAuthoringDatasourceDocument(content) {
	return !!(
		_.isPlainObject(content) &&
		_.isNumber(content.schemaVersion) &&
		_.isPlainObject(content.collections) &&
		_.isPlainObject(content.relationships)
	);
}

function inferPreferredKey(preferredKey = "", commonKeys = [], allKeys = [], candidates = []) {
	if (_.isString(preferredKey) && preferredKey.trim() !== "" && allKeys.includes(preferredKey.trim())) {
		return preferredKey.trim();
	}
	const commonMatch = candidates.find((candidate) => commonKeys.includes(candidate));
	if (commonMatch) {
		return commonMatch;
	}
	return candidates.find((candidate) => allKeys.includes(candidate)) || "";
}

export function inferGenericDatasourceKeys(rows = [], overlayEntry = null) {
	const { allKeys, commonKeys } = deriveCommonAndAllKeys(rows);
	const rowIdKey = inferPreferredKey(_.get(overlayEntry, "rowIdKey", ""), commonKeys, allKeys, ROW_ID_KEY_CANDIDATES);
	const labelKey = inferPreferredKey(_.get(overlayEntry, "labelKey", ""), commonKeys, allKeys, LABEL_KEY_CANDIDATES);
	const valueKey = inferPreferredKey(_.get(overlayEntry, "valueKey", ""), commonKeys, allKeys, VALUE_KEY_CANDIDATES);
	return {
		rowIdKey,
		labelKey: labelKey || rowIdKey || valueKey,
		valueKey: valueKey || rowIdKey || labelKey,
		allKeys,
		commonKeys
	};
}

export function getStableRowIdentity(row = {}, rowIdKey = "") {
	if (!_.isPlainObject(row)) {
		return "";
	}
	if (rowIdKey) {
		const explicitValue = _.get(row, rowIdKey, undefined);
		if (!_.isNil(explicitValue) && `${explicitValue}`.trim() !== "") {
			return `${explicitValue}`;
		}
	}
	const fallbackValue = !_.isNil(row.id)
		? row.id
		: !_.isNil(row.value)
			? row.value
			: !_.isNil(row.key)
				? row.key
				: null;
	if (!_.isNil(fallbackValue) && `${fallbackValue}`.trim() !== "") {
		return `${fallbackValue}`;
	}
	return "";
}

export function getDatasourceCollection(content, sourcePath = "") {
	const normalizedPath = normalizeDatasourcePath(sourcePath);
	return normalizedPath ? _.get(content, normalizedPath) : content;
}

export function getInlineVisualOverlayEntry(content, sourcePath = "") {
	if (!_.isPlainObject(content)) {
		return null;
	}
	return _.get(content, [INLINE_VISUAL_OVERLAY_KEY, getInlineVisualOverlayPathKey(sourcePath)], null);
}

function mergeRowWithInlineVisualOverlay(row, overlayEntry, meta) {
	if (!_.isPlainObject(row)) {
		return row;
	}
	const rowId = getStableRowIdentity(row, meta.rowIdKey);
	if (!rowId) {
		return row;
	}
	const overlayPayload = _.get(overlayEntry, ["rowsById", rowId], null);
	if (!_.isPlainObject(overlayPayload) || _.isEmpty(overlayPayload)) {
		return row;
	}
	return {
		...row,
		..._.cloneDeep(overlayPayload)
	};
}

export function mergeCollectionWithInlineVisualOverlay(content, sourcePath = "", providedCollection) {
	const collection = _.isUndefined(providedCollection) ? getDatasourceCollection(content, sourcePath) : providedCollection;
	const overlayEntry = getInlineVisualOverlayEntry(content, sourcePath);
	if (!_.isPlainObject(overlayEntry) || !_.isPlainObject(overlayEntry.rowsById) || _.isEmpty(overlayEntry.rowsById)) {
		return collection;
	}
	const sampleRows = collectObjectRows(collection, { annotateMapKeys: true });
	const meta = inferGenericDatasourceKeys(sampleRows, overlayEntry);
	if (!meta.rowIdKey) {
		return collection;
	}
	if (_.isArray(collection)) {
		return collection.map((entry) => (_.isPlainObject(entry) ? mergeRowWithInlineVisualOverlay(entry, overlayEntry, meta) : entry));
	}
	if (_.isPlainObject(collection)) {
		return _.mapValues(collection, (entry) => {
			if (_.isArray(entry)) {
				return entry.map((row) => (_.isPlainObject(row) ? mergeRowWithInlineVisualOverlay(row, overlayEntry, meta) : row));
			}
			if (_.isPlainObject(entry)) {
				return mergeRowWithInlineVisualOverlay(entry, overlayEntry, meta);
			}
			return entry;
		});
	}
	return collection;
}

export function getGenericDatasourceRows(content, sourcePath = "") {
	const normalizedPath = normalizeDatasourcePath(sourcePath);
	const collection = getDatasourceCollection(content, normalizedPath);
	const mergedCollection = mergeCollectionWithInlineVisualOverlay(content, normalizedPath, collection);
	const rows = collectObjectRows(mergedCollection, { annotateMapKeys: true });
	const overlayEntry = getInlineVisualOverlayEntry(content, normalizedPath);
	const meta = inferGenericDatasourceKeys(rows, overlayEntry);
	const rowsWithMeta = rows.map((row, index) => {
		const rowId = getStableRowIdentity(row, meta.rowIdKey);
		return {
			...row,
			__rowId: rowId,
			__rowIndex: index,
			__rowIdentity: rowId ? `row:${rowId}` : `row-index:${index}`,
			__sourcePath: normalizedPath
		};
	});
	return {
		sourcePath: normalizedPath,
		collection: mergedCollection,
		rows: rowsWithMeta,
		rowIdKey: meta.rowIdKey,
		labelKey: meta.labelKey,
		valueKey: meta.valueKey,
		allKeys: meta.allKeys,
		commonKeys: meta.commonKeys,
		overlayEntry
	};
}

export function collectGenericMapperCandidates(content) {
	if (isCanonicalAuthoringDatasourceDocument(content)) {
		return AUTHORING_COLLECTION_CANDIDATES.reduce((acc, config) => {
			const collection = _.get(content, config.path);
			if (!_.isPlainObject(collection)) {
				return acc;
			}
			const selection = getGenericDatasourceRows(content, config.path);
			acc.push({
				path: config.path,
				type: "mapOfObjects",
				groups: _.keys(collection).length,
				rowCount: selection.rows.length,
				allKeys: selection.allKeys,
				commonKeys: selection.commonKeys,
				rowIdKey: config.rowIdKey || selection.rowIdKey,
				labelKey: config.labelKey || selection.labelKey,
				valueKey: config.valueKey || selection.valueKey,
				displayLabel: config.displayLabel,
				description: config.description,
				visualStorage: AUTHORING_VISUAL_STORAGE_MODE,
				supportsMapper: true
			});
			return acc;
		}, []);
	}

	return collectDatasourcePathCandidates(content).map((candidate) => {
		const selection = getGenericDatasourceRows(content, candidate.path);
		return {
			...candidate,
			rowIdKey: selection.rowIdKey,
			labelKey: selection.labelKey,
			valueKey: selection.valueKey,
			visualStorage: OVERLAY_VISUAL_STORAGE_MODE,
			supportsMapper: !!selection.rowIdKey
		};
	});
}

function getMapObjectEntryKey(collection, row = null, rowIdKey = "", explicitRowId = "") {
	if (!_.isPlainObject(collection)) {
		return "";
	}
	const rowId = explicitRowId || getStableRowIdentity(row, rowIdKey);
	if (!rowId) {
		return "";
	}
	if (_.has(collection, rowId) && _.isPlainObject(collection[rowId])) {
		return rowId;
	}

	let matchedKey = "";
	_.forEach(collection, (entry, mapKey) => {
		if (!_.isPlainObject(entry)) {
			return;
		}
		if (getStableRowIdentity(entry, rowIdKey) === rowId) {
			matchedKey = mapKey;
			return false;
		}
		return null;
	});
	return matchedKey;
}

export function updateInlineEntityVisualsForRow(parsedContent, sourcePath = "", row = null, inlinePayload = {}, managedKeys = [], meta = {}) {
	const nextContent = _.cloneDeep(parsedContent || {});
	const normalizedPath = normalizeDatasourcePath(sourcePath);
	const collection = getDatasourceCollection(nextContent, normalizedPath);
	if (!_.isPlainObject(collection)) {
		return nextContent;
	}

	const rowIdKey = meta.rowIdKey || "id";
	const rowId = _.isString(meta.rowId) && meta.rowId !== "" ? meta.rowId : getStableRowIdentity(row, rowIdKey);
	const entryKey = getMapObjectEntryKey(collection, row, rowIdKey, rowId);
	if (!entryKey || !_.isPlainObject(collection[entryKey])) {
		return nextContent;
	}

	const nextEntry = _.cloneDeep(collection[entryKey]);
	managedKeys.forEach((key) => {
		delete nextEntry[key];
	});
	_.forEach(inlinePayload, (value, key) => {
		if (_.isNil(value) || value === "") {
			return;
		}
		nextEntry[key] = value;
	});
	collection[entryKey] = nextEntry;
	return nextContent;
}

export function updateMappedRowVisuals(parsedContent, sourcePath = "", row = null, visualPayload = {}, managedKeys = [], candidate = null) {
	if (candidate?.visualStorage === AUTHORING_VISUAL_STORAGE_MODE) {
		return updateInlineEntityVisualsForRow(
			parsedContent,
			sourcePath,
			row,
			visualPayload,
			managedKeys,
			{
				rowIdKey: candidate.rowIdKey,
				labelKey: candidate.labelKey,
				valueKey: candidate.valueKey
			}
		);
	}

	return updateInlineVisualOverlayForRow(
		parsedContent,
		sourcePath,
		row,
		visualPayload,
		managedKeys,
		candidate
			? {
				rowIdKey: candidate.rowIdKey,
				labelKey: candidate.labelKey,
				valueKey: candidate.valueKey
			}
			: {}
	);
}

export function updateInlineVisualOverlayForRow(parsedContent, sourcePath = "", row = null, overlayPayload = {}, managedKeys = [], meta = {}) {
	const nextContent = _.cloneDeep(parsedContent || {});
	const normalizedPath = normalizeDatasourcePath(sourcePath);
	const overlayPathKey = getInlineVisualOverlayPathKey(normalizedPath);
	const collection = getDatasourceCollection(nextContent, normalizedPath);
	const existingOverlayEntry = getInlineVisualOverlayEntry(nextContent, normalizedPath) || {};
	const sampleRows = collectObjectRows(collection, { annotateMapKeys: true });
	const inferredMeta = inferGenericDatasourceKeys(sampleRows, existingOverlayEntry);
	const rowIdKey = meta.rowIdKey || existingOverlayEntry.rowIdKey || inferredMeta.rowIdKey || "";
	const rowId = _.isString(meta.rowId) && meta.rowId !== "" ? meta.rowId : getStableRowIdentity(row, rowIdKey);
	if (!rowId) {
		return nextContent;
	}

	if (!_.isPlainObject(nextContent[INLINE_VISUAL_OVERLAY_KEY])) {
		nextContent[INLINE_VISUAL_OVERLAY_KEY] = {};
	}

	const nextOverlayEntry = {
		..._.cloneDeep(existingOverlayEntry),
		rowIdKey,
		labelKey: meta.labelKey || existingOverlayEntry.labelKey || inferredMeta.labelKey || "",
		valueKey: meta.valueKey || existingOverlayEntry.valueKey || inferredMeta.valueKey || "",
		rowsById: _.isPlainObject(existingOverlayEntry.rowsById) ? _.cloneDeep(existingOverlayEntry.rowsById) : {}
	};

	const nextRowOverlay = _.isPlainObject(nextOverlayEntry.rowsById[rowId]) ? _.cloneDeep(nextOverlayEntry.rowsById[rowId]) : {};
	managedKeys.forEach((key) => {
		delete nextRowOverlay[key];
	});
	_.forEach(overlayPayload, (value, key) => {
		if (_.isNil(value) || value === "") {
			return;
		}
		nextRowOverlay[key] = value;
	});

	if (_.isEmpty(nextRowOverlay)) {
		delete nextOverlayEntry.rowsById[rowId];
	} else {
		nextOverlayEntry.rowsById[rowId] = nextRowOverlay;
	}

	nextContent[INLINE_VISUAL_OVERLAY_KEY][overlayPathKey] = nextOverlayEntry;
	return nextContent;
}
